import { FC, useEffect, useState } from 'react';

import { useConfirm } from 'material-ui-confirm';
import { Edit3, Lock, Trash2 } from 'react-feather';

import {
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { useGetPaginateUsers, useUserDelete } from '../../../api-http/users';
import { DialogModal } from '../../../components';
import { AlertBar } from '../../../components/AlertBar';

import { BaseUserWithOrg } from '../../../types/user.types';
import { displayDate } from '../../../util/dateTime';
import { useUserRoleName } from '../../../util/useUserTypes';
import { useSnackbar } from 'notistack';
import { ResetPasswordForm } from './ResetPasswordForm';
import { UserUpdateForm } from './UserUpdateForm';
import { notifyMsg } from '../../../configs';
import { ERROR, SUCCESS } from '../../../util/constant';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../../components/TableTheme';
import { useTable } from '../../../util/useTable';
import { OrderEnum, SortEnum } from '../../../types/pagination.types';
import { managementTableSx } from '../../../components/ManagementPageLayout';

interface UserTableProp {
  selectedOrgId: string;
  search: string;
  refresh: boolean;
}

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

export const UserTable: FC<UserTableProp> = ({
  selectedOrgId,
  search,
  refresh,
}) => {
  const confirm = useConfirm();

  const { sortOnClick, pagingParams, tableProps } = useTable();
  const { sortby, order } = pagingParams;
  const {
    data: list,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useGetPaginateUsers({
    ...pagingParams,
    search,
    organization: selectedOrgId,
  });

  const {
    isLoading: isLoadingUserDelete,
    error: errorUserDelete,
    mutate,
    isSuccess,
    isError,
  } = useUserDelete();

  const [activeUser, setActiveUser] = useState<BaseUserWithOrg | undefined>();
  const [renameUserModal, setRenameUserModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [userResetPasswordModal, setUserResetPasswordModal] = useState(false);

  useEffect(() => {
    refetch();
    if (isSuccess) {
      enqueueSnackbar(notifyMsg.USER_DELETE_SUCCESS, {
        variant: SUCCESS,
      });
    }
    if (isError) {
      enqueueSnackbar(notifyMsg.DELETE_ATTEMPT_FAILED, {
        variant: ERROR,
      });
    }
  }, [isSuccess, isError, search, refresh]);

  const handleDelete = async (user: BaseUserWithOrg) => {
    try {
      await confirm({
        description: 'Are you sure you want to delete?',
        confirmationText: 'Yes',
      });

      mutate({ id: user._id });
    } catch (error) {}
  };

  return (
    <>
      {(isLoading || isLoadingUserDelete || isRefetching) && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg || errorUserDelete?.msg} />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 760 }} aria-label="Organization users">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.name || true}
                  direction={sortby === SortEnum.name ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.name)}
                >
                  User Name
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell>Role</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt || true}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt || true}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Created
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list?.data.map((user) => {
              return (
                <TableRow key={user._id} sx={{ height: 56 }}>
                  <TableCell component="th" scope="row">
                    {user.name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {useUserRoleName(user.role)}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{displayDate(user.updatedAt)}</TableCell>
                  <TableCell>{displayDate(user.createdAt)}</TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Edit user">
                        <IconButton
                          aria-label={`Edit ${user.name}`}
                          onClick={() => {
                            setActiveUser(user);
                            setRenameUserModal(true);
                          }}
                          sx={{ ...actionButtonSx, color: '#2563eb' }}
                        >
                          <Edit3 size={15} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset password">
                        <IconButton
                          aria-label={`Reset password for ${user.name}`}
                          onClick={() => {
                            setActiveUser(user);
                            setUserResetPasswordModal(true);
                          }}
                          sx={{ ...actionButtonSx, color: '#64748b' }}
                        >
                          <Lock size={15} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete user">
                        <IconButton
                          aria-label={`Delete ${user.name}`}
                          onClick={() => handleDelete(user)}
                          sx={{
                            ...actionButtonSx,
                            color: '#ef4444',
                            borderColor: '#fecaca',
                            bgcolor: '#fffafa',
                          }}
                        >
                          <Trash2 size={15} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>
      <DialogModal
        onClose={() => setRenameUserModal(false)}
        open={renameUserModal}
        title="Edit user"
      >
        <UserUpdateForm
          profileData={activeUser && activeUser}
          onFinished={() => {
            setRenameUserModal(false);
            refetch();
          }}
        />
      </DialogModal>
      <DialogModal
        onClose={() => setUserResetPasswordModal(false)}
        open={userResetPasswordModal}
        title="Reset password"
      >
        <ResetPasswordForm
          onFinished={() => setUserResetPasswordModal(false)}
          userData={activeUser}
        />
      </DialogModal>
    </>
  );
};
